import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';
import { ResponsiveContainer } from 'recharts';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import { TooltipIcon } from '../../../shared-components/tooltip/TooltipIcon';
import HexToRgba from '../../../shared-functions/ConvertHexToRGB';
import accountApi from '../../../utils/api/account';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  totalRevenue: number;
  totalAmountPaid: number;
  totalPayableAmount: number;
  monthName?: string;
  month?: string | number;
  year?: string | number;
  date?: string;
  day?: string | number;
  week?: string | number;
  weekName?: string;
  hour?: number | string;
  time?: string;
  [key: string]: any;
}

/**
 * Month translation mapping dictionary.
 */
const monthMap: Record<string, string> = {
  Jan: 'jan',
  Feb: 'feb',
  Mar: 'mar',
  Apr: 'apr',
  May: 'may_month',
  Jun: 'jun',
  Jul: 'jul',
  Aug: 'aug',
  Sep: 'sep',
  Oct: 'oct',
  Nov: 'nov',
  Dec: 'dec',
};

/**
 * Short month names array for month resolution.
 */
const shortMonths: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Formats a chart label based on whether it represents an hour, day/date, week, or month.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {Function} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @param {string} [startDate] - Optional start date of current range.
 * @param {string} [endDate] - Optional end date of current range.
 * @returns {string} Formatted label string for the chart axis.
 */
const formatChartLabel = (
  item: ChartDataProperties,
  t: any,
  locale: string = 'en',
  startDate?: string,
  endDate?: string,
): string => {
  // 1. Hour / Time format handling (e.g. { hour: 10, time: "10:00", date: "2026-08-31" })
  if (item.time !== undefined && item.time !== null && item.time !== '') {
    return String(item.time).trim().slice(0, 5);
  }
  if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    if (!Number.isNaN(hourNumber)) {
      return `${String(hourNumber).padStart(2, '0')}:00`;
    }
    return String(item.hour).trim();
  }

  // 2. Specific Date / Day handling (takes priority over standalone month)
  let parsedDate: Date | null = null;
  const rawDate = item.date;

  if (rawDate !== undefined && rawDate !== null && rawDate !== '') {
    const dateString = String(rawDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [yearString, monthPart, dayString] = dateString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [dayString, monthPart, yearString] = dateString.split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (dateString.includes('-') || dateString.includes('/')) {
      const generalDate = new Date(dateString);
      if (!Number.isNaN(generalDate.getTime())) {
        parsedDate = generalDate;
      }
    }
  }

  // If parsed date was not found via rawDate, check day + month numbers
  if (!parsedDate && item.day !== undefined && item.day !== null && item.day !== '') {
    const dayNumber = Number(item.day);
    const rawMonthValue = item.month ?? item.monthName;
    const monthNumber =
      rawMonthValue !== undefined && rawMonthValue !== null && rawMonthValue !== ''
        ? Number(rawMonthValue)
        : NaN;

    if (!Number.isNaN(dayNumber) && dayNumber >= 1 && dayNumber <= 31) {
      let resolvedMonth =
        !Number.isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12 ? monthNumber : NaN;

      if (Number.isNaN(resolvedMonth) && typeof rawMonthValue === 'string') {
        const monthIndex = shortMonths.findIndex(
          (m) => m.toLowerCase() === rawMonthValue.trim().slice(0, 3).toLowerCase(),
        );
        if (monthIndex !== -1) {
          resolvedMonth = monthIndex + 1;
        }
      }

      if (!Number.isNaN(resolvedMonth)) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, resolvedMonth - 1, dayNumber);
        if (!Number.isNaN(dateObject.getTime())) {
          parsedDate = dateObject;
        }
      }
    }
  }

  if (parsedDate) {
    const isMultiYear =
      Boolean(startDate) &&
      Boolean(endDate) &&
      new Date(startDate!).getFullYear() !== new Date(endDate!).getFullYear();

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      ...(isMultiYear ? { year: '2-digit' } : {}),
    }).format(parsedDate);
  }

  // 3. Week format handling
  const weekValue = item.weekName || item.week;
  if (weekValue !== undefined && weekValue !== null && weekValue !== '') {
    const weekString = String(weekValue).trim();
    const weekMatch = weekString.match(/^week\s*(\d+)$/i) || weekString.match(/^w\s*(\d+)$/i);
    if (weekMatch) {
      return `${t('week', { defaultValue: 'Week' })} ${weekMatch[1]}`;
    }
    if (/^\d+$/.test(weekString)) {
      return `${t('week', { defaultValue: 'Week' })} ${weekString}`;
    }
    return weekString;
  }

  // 4. Month format handling (monthly aggregate without day)
  const rawMonth = item.monthName || item.month;
  if (rawMonth !== undefined && rawMonth !== null && rawMonth !== '') {
    const monthString = String(rawMonth).trim();

    // Check numeric month 1-12 or "01"-"12"
    if (/^\d{1,2}$/.test(monthString)) {
      const monthNumber = Number(monthString);
      if (monthNumber >= 1 && monthNumber <= 12) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, monthNumber - 1, 1);
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return item.year ? `${monthShort} ${item.year}` : monthShort;
      }
    }

    // Check "Jan 2024" or "Jan 24" format
    const parts = monthString.split(' ');
    if (parts.length === 2 && monthMap[parts[0]]) {
      return `${t(monthMap[parts[0]])} ${parts[1]}`;
    }

    // Check "Jan", "Feb" format
    if (monthMap[monthString]) {
      return t(monthMap[monthString]);
    }

    // Check "YYYY-MM" format e.g. "2026-08"
    if (/^\d{4}-\d{2}$/.test(monthString)) {
      const [year, month] = monthString.split('-').map(Number);
      const dateObject = new Date(year, month - 1, 1);
      if (!Number.isNaN(dateObject.getTime())) {
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return `${monthShort} ${year}`;
      }
    }

    return monthString;
  }

  // 5. Fallback on label
  if (item.label !== undefined && item.label !== null && item.label !== '') {
    const labelString = String(item.label).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(labelString)) {
      const [yearString, monthPart, dayString] = labelString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        return new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: 'short',
        }).format(dateObject);
      }
    }
    if (/^\d{1,2}$/.test(labelString)) {
      const numericMonth = Number(labelString);
      if (numericMonth >= 1 && numericMonth <= 12) {
        const dateObject = new Date(2000, numericMonth - 1, 1);
        return new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
      }
    }
    if (monthMap[labelString]) {
      return t(monthMap[labelString]);
    }
    return labelString;
  }

  return '';
};

/**
 * Formats a detailed title string for the chart tooltip.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {Function} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @returns {string} Formatted tooltip title string.
 */
const getTooltipTitle = (item: ChartDataProperties, t: any, locale: string = 'en'): string => {
  // 1. Time / Hour with date
  if (item.date && item.time) {
    return `${item.date} ${item.time}`;
  }
  if (item.date && item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    const hourPad = !Number.isNaN(hourNumber)
      ? `${String(hourNumber).padStart(2, '0')}:00`
      : String(item.hour);
    return `${item.date} ${hourPad}`;
  }

  // 2. Specific date
  if (item.date && /^\d{4}-\d{2}-\d{2}/.test(String(item.date))) {
    const [yearString, monthPart, dayString] = String(item.date).split('T')[0].split('-');
    const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
    if (!Number.isNaN(dateObject.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  // 3. Day + Month
  if (item.day !== undefined && item.day !== '' && item.month !== undefined && item.month !== '') {
    const d = Number(item.day);
    const m = Number(item.month);
    const y = Number(item.year) || new Date().getFullYear();
    if (!Number.isNaN(d) && !Number.isNaN(m) && m >= 1 && m <= 12) {
      const dateObject = new Date(y, m - 1, d);
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  // 4. Month with Year
  if (item.month && item.year) {
    const m = Number(item.month);
    const y = Number(item.year);
    if (!Number.isNaN(m) && !Number.isNaN(y) && m >= 1 && m <= 12) {
      const dateObject = new Date(y, m - 1, 1);
      return new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  return item.label || '';
};

/**
 * Revenue vs Payable vs Net Payable vs Payout Chart.
 * Displays revenue, payable, and payout data across day, week, or month time intervals.
 * @returns {any} The rendered chart component.
 */
function RevenueVsPayableChart() {
  const { t, i18n } = useTranslation(['account', 'customDate', 'station', 'userRole']);
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  // COLORS — read from theme CSS variables for light/dark support
  const rootStyles = getComputedStyle(document.documentElement);
  const axisColor = rootStyles.getPropertyValue('--chart-axis-color').trim();
  const labelColor = rootStyles.getPropertyValue('--chart-label-color').trim();
  const tooltipBgColor = rootStyles.getPropertyValue('--chart-tooltip-bg-color').trim();
  const tooltipTextColor = rootStyles.getPropertyValue('--chart-tooltip-text-color').trim();
  const revenueColor = rootStyles.getPropertyValue('--revenue-color').trim();

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        rtl: i18n.language === 'ar',
        labels: {
          /**
           * Hides all legend items.
           * @returns {boolean} False to hide.
           */
          filter: () => false,
        },
      },
      tooltip: {
        backgroundColor: tooltipBgColor,
        titleColor: tooltipTextColor,
        bodyColor: tooltipTextColor,
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        rtl: i18n.language === 'ar',
        textDirection: i18n.language === 'ar' ? 'rtl' : 'ltr',
        callbacks: {
          /**
           * Formats the tooltip title using the detailed date format.
           * @param {any[]} tooltipItems - The tooltip items.
           * @returns {string} The tooltip title string.
           */
          title: (tooltipItems: any[]): string => {
            const index = tooltipItems?.[0]?.dataIndex;
            const item = graphData[index];
            return item ? getTooltipTitle(item, t, i18n.language) : '';
          },
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        reverse: i18n.language === 'ar',
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: labelColor,
          autoSkip: true,
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        grid: { display: false }, // remove Y grid lines
        border: { display: false }, // remove Y axis line
        ticks: { display: false }, // remove Y axis numbers
      },
    },
  };

  /** Draws the vertical crosshair line in black on hover, overriding the default dataset color */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance.
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = axisColor;
        context.stroke();
        context.restore();
      }
    },
  };

  /**
   * Fetches the revenue vs payable report data for the specified time range and updates the chart.
   * @returns {Promise<void>} Resolves when data is fetched and state updated.
   */
  const fetchReport = async (): Promise<void> => {
    if (!accountCustomId) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await accountApi.fetchRevenuePayable(accountCustomId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      const responseData = response?.data;
      let rawList: any[] = [];
      if (Array.isArray(responseData)) {
        rawList = responseData;
      } else if (Array.isArray(responseData?.results)) {
        rawList = responseData.results;
      } else if (Array.isArray(responseData?.result)) {
        rawList = responseData.result;
      } else if (Array.isArray(responseData?.data)) {
        rawList = responseData.data;
      }

      const formattedData: ChartDataProperties[] = rawList.map((d: any) => {
        const day = d.day ?? d._id?.day;
        const month = d.month ?? d._id?.month;
        const monthName = d.monthName ?? d._id?.monthName;
        const year = d.year ?? d._id?.year;
        const hour = d.hour ?? d._id?.hour;
        const time = d.time ?? d._id?.time;

        let date =
          d.date ??
          d._id?.date ??
          (typeof d._id === 'string' && d._id.includes('-') ? d._id : undefined) ??
          d.createdAt;

        // If date is not provided, but day and month are present, construct standard ISO date string
        if (
          !date &&
          day !== undefined &&
          day !== null &&
          day !== '' &&
          month !== undefined &&
          month !== null &&
          month !== ''
        ) {
          const dNumber = Number(day);
          const mNumber = Number(month);
          const fallbackYear = dateRange.startDate
            ? new Date(dateRange.startDate).getFullYear()
            : new Date().getFullYear();
          const yNumber = Number(year) || fallbackYear;
          if (
            !Number.isNaN(dNumber) &&
            !Number.isNaN(mNumber) &&
            mNumber >= 1 &&
            mNumber <= 12 &&
            dNumber >= 1 &&
            dNumber <= 31
          ) {
            date = `${yNumber}-${String(mNumber).padStart(2, '0')}-${String(dNumber).padStart(2, '0')}`;
          }
        }

        return {
          label:
            time ??
            (hour !== undefined && hour !== null
              ? `${String(hour).padStart(2, '0')}:00`
              : undefined) ??
            d.label ??
            date ??
            d.weekName ??
            d.week ??
            monthName ??
            month ??
            '',
          totalRevenue: Number(d.totalRevenue ?? d.revenue ?? d.totalDebitAmount ?? 0),
          totalAmountPaid: Number(
            d.totalAmountPaid ??
              d.totalPaidRevenue ??
              d.amountPaid ??
              d.totalPaidAmount ??
              d.payout ??
              0,
          ),
          totalPayableAmount: Number(
            d.totalPayableAmount ?? d.payableAmount ?? d.totalPayable ?? d.payable ?? 0,
          ),
          hour,
          time,
          year,
          monthName: monthName ?? '',
          month: month ?? '',
          date: date ?? '',
          day: day ?? '',
          week: d.week ?? d.weekNumber ?? '',
          weekName: d.weekName ?? '',
        };
      });

      formattedData.sort((a, b) => {
        if (a.date && b.date) {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        if (a.hour !== undefined && b.hour !== undefined) {
          return Number(a.hour) - Number(b.hour);
        }
        return 0;
      });

      setGraphData(formattedData);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
      setGraphData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [accountCustomId, i18n.language, dateRange.startDate, dateRange.endDate]);

  /**
   * Generates formatted chart labels for the x-axis based on day, week, or month data.
   */
  const labels = graphData.map((d) =>
    formatChartLabel(d, t, i18n.language, dateRange.startDate, dateRange.endDate),
  );

  return (
    <div className="power-consumed-chart" dir={i18n.dir()}>
      <div className="power-consumed-container">
        <div className="report-header header">
          <div className="report-title">
            <div className="title-wrapper">
              <p>
                {t('revenue')}
              </p>
              <TooltipIcon text={t('revenue')} infoColor="info" />
            </div>
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
              placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
              defaultPreset="last14Days"
            />
          </div>
        </div>
        <div className="power-consumed-container">
          {isLoading ? (
            <div className="loader-wrap">
              <BatteryLoader />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <Line
                data={{
                  labels,
                  datasets: [
                    {
                      label: t('revenue'),
                      data: graphData.map((d) => d.totalRevenue),
                      borderColor: revenueColor,
                      backgroundColor: HexToRgba(revenueColor, 0.2),
                      borderWidth: 1.5,
                      fill: false,
                      tension: 0.4,
                      pointRadius: 0,
                      pointHoverRadius: 0,
                    },
                  ],
                }}
                options={chartOptions}
                plugins={[verticalHoverLinePlugin]}
              />
            </ResponsiveContainer>
          )}
        </div>

        <div className="chart-label">
          <div className="each-label">
            <div className="each-label-color revenue-color" />
            <p>{t('revenue')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RevenueVsPayableChart;
